/*
  Upload card for the ticket-import page. Renders the antd Dragger when no
  file has been parsed; once parsed it collapses to a single-line summary
  with Replace / Clear / Format help controls.
*/
import React, { useRef } from 'react';
import { Button, Card, Col, Popover, Row, Space, Tag, Typography, Upload } from 'antd';
import { CheckCircleTwoTone, InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { isValidString } from '../../utils/utilities';
import { OPTIONAL_HEADERS, REQUIRED_HEADERS } from './constants';

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

const FormatHelpContent = () => (
    <div style={{ maxWidth: 360 }}>
        <Paragraph style={{ marginBottom: 8 }}>
            Headers are matched by name (case-insensitive) — column order doesn't matter.
        </Paragraph>
        <Text strong>Required</Text>
        <div style={{ margin: '4px 0 8px' }}>
            <Space size={[4, 4]} wrap>
                {REQUIRED_HEADERS.map((header) => (
                    <Tag color="red" key={header}>{header}</Tag>
                ))}
            </Space>
        </div>
        <Text strong>Optional</Text>
        <div style={{ margin: '4px 0 0' }}>
            <Space size={[4, 4]} wrap>
                {OPTIONAL_HEADERS.map((header) => (
                    <Tag key={header}>{header}</Tag>
                ))}
            </Space>
        </div>
    </div>
);

export const UploadCard = ({
    hasParsed,
    fileName,
    parsedRowsCount,
    ticketsCount,
    tripsCount,
    processFile,
    onClear,
}) => {
    const fileInputRef = useRef(null);

    const uploadProps = {
        accept: '.xlsx,.xls,.csv',
        multiple: false,
        showUploadList: false,
        beforeUpload: (file) => {
            processFile(file);
            return false;
        },
    };

    return (
        <Card>
            {!hasParsed ? (
                <Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                        Drag your export file here or click to browse
                    </p>
                    <p className="ant-upload-hint">
                        Accepted formats: .xlsx, .xls, .csv
                        {' · '}
                        <Popover content={<FormatHelpContent />} title="Expected columns" trigger="click">
                            <a onClick={(e) => e.stopPropagation()}>view required headers</a>
                        </Popover>
                    </p>
                </Dragger>
            ) : (
                <Row justify="space-between" align="middle" gutter={[16, 8]}>
                    <Col>
                        <Space>
                            <CheckCircleTwoTone twoToneColor="#52c41a" />
                            <Text strong>{fileName}</Text>
                            <Text type="secondary">
                                {`· ${parsedRowsCount} row(s) · ${ticketsCount} ticket(s) · ${tripsCount} trip(s)`}
                            </Text>
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                style={{ display: 'none' }}
                                onChange={(event) => {
                                    const file = event.target.files && event.target.files[0];
                                    if (file) {
                                        processFile(file);
                                    }
                                    event.target.value = '';
                                }}
                            />
                            <Popover content={<FormatHelpContent />} title="Expected columns" trigger="click">
                                <Button type="link">Format help</Button>
                            </Popover>
                            <Button
                                icon={<UploadOutlined />}
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            >
                                Replace file
                            </Button>
                            <Button onClick={onClear}>Clear</Button>
                        </Space>
                    </Col>
                </Row>
            )}
        </Card>
    );
};

// Silence unused-import warning.
void isValidString;
